/**
 * Barra de filtros do Papel de trabalho da conta.
 *
 * Por que existe: a conta 856/201 gera 22.095 itens. Sem filtro, achar "o
 * lançamento de mais de mil reais de março" é rolar 22 mil linhas.
 *
 * REGRA DE OURO DESTA TELA: o filtro recorta a GRADE, nunca o TOTAL DA
 * COMPOSIÇÃO nem a linha de conciliação — esses seguem sendo da conta inteira.
 * A soma do recorte aparece em bloco próprio e rotulado ("soma do filtro"), que
 * é responsabilidade do `index.tsx`. Aqui só coletamos a intenção do usuário.
 *
 * Os filtros só são APLICADOS quando o usuário confirma (botão/Enter), não a
 * cada tecla: cada aplicação é uma ida ao backend que recompõe o saldo de uma
 * conta de 22 mil itens.
 */

import React from 'react';
import { Badge, Box, Button, Collapse, Flex, Grid, Input, Select, Text } from '@chakra-ui/react';
import { CaretDown, CaretUp, FunnelSimple, X } from 'phosphor-react';

import {
  FILTROS_VAZIOS,
  IFiltrosUI,
  OPERADORES,
  Operador,
  ROTULO_OPERADOR,
  contaFiltrosAtivos,
  resumoFiltrosAplicados,
} from './papelTrabalho';

const COR = {
  ink: '#0F172A',
  muted: '#64748B',
  brand: '#1B88F5',
  linha: 'rgba(15, 23, 42, 0.10)',
} as const;

interface Props {
  /** Rascunho: o que o usuário está digitando (ainda não aplicado). */
  rascunho: IFiltrosUI;
  onChange: (f: IFiltrosUI) => void;
  onAplicar: () => void;
  onLimpar: () => void;
  /** Filtros de fato aplicados — alimenta o badge e o botão "limpar". */
  aplicados: IFiltrosUI;
  carregando?: boolean;
  /** Bloco expandido? Controlado pela tela (que persiste a preferência). */
  aberto: boolean;
  onAlternar: () => void;
}

const ROTULO_CAMPO = {
  fontSize: '10px',
  fontWeight: '700',
  color: COR.muted,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
} as const;

export function FiltrosComposicao({
  rascunho,
  onChange,
  onAplicar,
  onLimpar,
  aplicados,
  carregando,
  aberto,
  onAlternar,
}: Props) {
  const set = (campo: keyof IFiltrosUI, valor: string) => onChange({ ...rascunho, [campo]: valor });

  const ativos = contaFiltrosAtivos(aplicados);
  // Recolhido, este texto é a ÚNICA pista do que está recortando a grade.
  const resumoAplicados = resumoFiltrosAplicados(aplicados);

  // Enter em qualquer campo aplica — é o gesto natural de quem está filtrando.
  const aoTeclar = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') onAplicar();
  };

  /** `entre` é o único operador que pede um segundo campo. */
  const mostraSegundoValor = rascunho.valor_op === 'entre';
  const mostraSegundaData = rascunho.data_op === 'entre';

  return (
    <Box
      mt={4}
      border="1px solid"
      borderColor={COR.linha}
      borderRadius="lg"
      px={3}
      py={3}
      bg="rgba(255, 255, 255, 0.55)"
    >
      <Flex align="center" gap={2} mb={aberto ? 3 : 0} wrap="wrap">
        {/* O cabeçalho inteiro é o gatilho de recolher/expandir — alvo grande,
            e o ícone sozinho seria um clique pequeno demais para o gesto. */}
        <Flex
          as="button"
          align="center"
          gap={2}
          flex="1"
          minW="200px"
          textAlign="left"
          onClick={onAlternar}
          aria-expanded={aberto}
          aria-label={aberto ? 'Recolher filtros' : 'Expandir filtros'}
          title={aberto ? 'Recolher filtros' : 'Expandir filtros'}
          {...{ type: 'button' }}
        >
          {aberto ? (
            <CaretUp size={13} weight="bold" color={COR.muted} />
          ) : (
            <CaretDown size={13} weight="bold" color={COR.muted} />
          )}
          <FunnelSimple size={15} weight="bold" color={COR.brand} />
          <Text fontSize="12px" fontWeight="700" color={COR.ink}>
            Filtrar itens da composição
          </Text>
          {/* O badge segue visível RECOLHIDO — é o que impede a tela de mostrar
              300 de 22.095 itens sem dizer por quê. Ver `resumoAplicados`. */}
          {ativos > 0 && (
            <Badge
              borderRadius="full"
              px={2}
              py={0.5}
              fontSize="10px"
              fontWeight="700"
              textTransform="none"
              bg="rgba(27, 136, 245, 0.12)"
              color={COR.brand}
            >
              {ativos} {ativos === 1 ? 'filtro ativo' : 'filtros ativos'}
            </Badge>
          )}
          {/* Recolhido, o badge sozinho diz QUANTOS mas não QUAIS. O resumo
              nomeia os campos para que o recorte não vire mistério. */}
          {!aberto && ativos > 0 && (
            <Text fontSize="11px" color={COR.muted} noOfLines={1} title={resumoAplicados}>
              {resumoAplicados}
            </Text>
          )}
        </Flex>

        {ativos > 0 && (
          <Button
            size="xs"
            variant="ghost"
            color={COR.muted}
            fontWeight="600"
            leftIcon={<X size={12} weight="bold" />}
            onClick={onLimpar}
            isDisabled={carregando}
          >
            Limpar filtros
          </Button>
        )}
        {/* "Aplicar" só faz sentido com os campos à vista: recolhido não há o
            que aplicar, e o botão competiria com "Limpar filtros". */}
        {aberto && (
          <Button
            size="xs"
            bg={COR.brand}
            color="white"
            fontWeight="700"
            borderRadius="md"
            _hover={{ bg: '#1573D1' }}
            onClick={onAplicar}
            isLoading={carregando}
            loadingText="Filtrando"
          >
            Aplicar
          </Button>
        )}
      </Flex>

      <Collapse in={aberto} animateOpacity>
        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }} gap={3}>
          {/* ---------------------------------------------- busca textual */}
          <Box>
            <Text sx={ROTULO_CAMPO}>Histórico</Text>
            <Input
              size="sm"
              mt={1}
              borderRadius="md"
              placeholder="Buscar no histórico..."
              value={rascunho.historico}
              onChange={e => set('historico', e.target.value)}
              onKeyDown={aoTeclar}
            />
          </Box>

          <Box>
            <Text sx={ROTULO_CAMPO}>Contrapartida</Text>
            <Input
              size="sm"
              mt={1}
              borderRadius="md"
              placeholder="Código ou nome"
              value={rascunho.contrapartida}
              onChange={e => set('contrapartida', e.target.value)}
              onKeyDown={aoTeclar}
            />
          </Box>

          <Box>
            <Text sx={ROTULO_CAMPO}>Conta (código exato)</Text>
            <Input
              size="sm"
              mt={1}
              borderRadius="md"
              placeholder="Ex.: 611"
              value={rascunho.conta_contrapartida}
              onChange={e => set('conta_contrapartida', e.target.value)}
              onKeyDown={aoTeclar}
            />
          </Box>

          <Box>
            <Text sx={ROTULO_CAMPO}>Documento</Text>
            <Input
              size="sm"
              mt={1}
              borderRadius="md"
              placeholder="Nº do documento"
              value={rascunho.documento}
              onChange={e => set('documento', e.target.value)}
              onKeyDown={aoTeclar}
            />
          </Box>

          {/* ------------------------------------------------- período */}
          <Box>
            <Text sx={ROTULO_CAMPO}>Período — de</Text>
            <Input
              size="sm"
              mt={1}
              borderRadius="md"
              type="date"
              value={rascunho.periodo_de}
              onChange={e => set('periodo_de', e.target.value)}
              onKeyDown={aoTeclar}
            />
          </Box>
          <Box>
            <Text sx={ROTULO_CAMPO}>Período — até</Text>
            <Input
              size="sm"
              mt={1}
              borderRadius="md"
              type="date"
              value={rascunho.periodo_ate}
              onChange={e => set('periodo_ate', e.target.value)}
              onKeyDown={aoTeclar}
            />
          </Box>

          <Box>
            <Text sx={ROTULO_CAMPO}>Tipo da linha</Text>
            <Select
              size="sm"
              mt={1}
              borderRadius="md"
              value={rascunho.tipo}
              onChange={e => set('tipo', e.target.value)}
            >
              <option value="">Todos</option>
              <option value="provisao">Em aberto (provisão)</option>
              <option value="pagamento_maior">Pagamento a maior</option>
              <option value="nao_identificado">Não identificado</option>
            </Select>
          </Box>

          <Box />

          {/* ------------------------------- filtro personalizado de VALOR */}
          <Box>
            <Text sx={ROTULO_CAMPO}>Valor</Text>
            <Select
              size="sm"
              mt={1}
              borderRadius="md"
              value={rascunho.valor_op}
              onChange={e => set('valor_op', e.target.value as Operador | '')}
              aria-label="Operador do filtro de valor"
            >
              <option value="">Sem filtro de valor</option>
              {OPERADORES.map(op => (
                <option key={op} value={op}>
                  {ROTULO_OPERADOR[op]}
                </option>
              ))}
            </Select>
          </Box>

          <Box>
            <Text sx={ROTULO_CAMPO}>{mostraSegundoValor ? 'Valor — de' : 'Valor'}</Text>
            <Input
              size="sm"
              mt={1}
              borderRadius="md"
              type="number"
              step="0.01"
              placeholder="0,00"
              value={rascunho.valor}
              isDisabled={!rascunho.valor_op}
              onChange={e => set('valor', e.target.value)}
              onKeyDown={aoTeclar}
              aria-label="Valor de referência"
            />
          </Box>

          {/* O segundo campo só existe no `entre` — mostrar sempre sugeriria que
            "maior que" precisa de dois valores. */}
          {mostraSegundoValor ? (
            <Box>
              <Text sx={ROTULO_CAMPO}>Valor — até</Text>
              <Input
                size="sm"
                mt={1}
                borderRadius="md"
                type="number"
                step="0.01"
                placeholder="0,00"
                value={rascunho.valor_ate}
                onChange={e => set('valor_ate', e.target.value)}
                onKeyDown={aoTeclar}
                aria-label="Valor final do intervalo"
              />
            </Box>
          ) : (
            <Box />
          )}

          <Box />

          {/* -------------------------------- filtro personalizado de DATA */}
          <Box>
            <Text sx={ROTULO_CAMPO}>Data do lançamento</Text>
            <Select
              size="sm"
              mt={1}
              borderRadius="md"
              value={rascunho.data_op}
              onChange={e => set('data_op', e.target.value as Operador | '')}
              aria-label="Operador do filtro de data"
            >
              <option value="">Sem filtro de data</option>
              {OPERADORES.map(op => (
                <option key={op} value={op}>
                  {ROTULO_OPERADOR[op]}
                </option>
              ))}
            </Select>
          </Box>

          <Box>
            <Text sx={ROTULO_CAMPO}>{mostraSegundaData ? 'Data — de' : 'Data'}</Text>
            <Input
              size="sm"
              mt={1}
              borderRadius="md"
              type="date"
              value={rascunho.data_filtro}
              isDisabled={!rascunho.data_op}
              onChange={e => set('data_filtro', e.target.value)}
              onKeyDown={aoTeclar}
              aria-label="Data de referência"
            />
          </Box>

          {mostraSegundaData ? (
            <Box>
              <Text sx={ROTULO_CAMPO}>Data — até</Text>
              <Input
                size="sm"
                mt={1}
                borderRadius="md"
                type="date"
                value={rascunho.data_filtro_ate}
                onChange={e => set('data_filtro_ate', e.target.value)}
                onKeyDown={aoTeclar}
                aria-label="Data final do intervalo"
              />
            </Box>
          ) : (
            <Box />
          )}
        </Grid>
      </Collapse>
    </Box>
  );
}

export { FILTROS_VAZIOS };
